import React, { useState, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { getConsentStatus, updateConsent, migrateConsentKey } from '@shared/seo/gtm';
import { config } from '../../config';
import styles from './CookieConsent.module.css';

const PRIVACY_POLICY_URL = 'https://umowsie.com.pl/privacy-policy/';

export const CookieConsent: React.FC = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const hasAnalytics = Boolean(config.gtmId);
  const siteName = config.siteName;

  useEffect(() => {
    migrateConsentKey();
    const consent = getConsentStatus();
    if (!consent) {
      const timer = setTimeout(() => setVisible(true), 500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    updateConsent(true);
    setVisible(false);
  };

  const handleDecline = () => {
    updateConsent(false);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className={styles.overlay} role="dialog" aria-labelledby="cookie-consent-title" aria-describedby="cookie-consent-desc">
      <div className={styles.banner}>
        <div className={styles.content}>
          <h2 id="cookie-consent-title" className={styles.title}>
            {t('cookieConsent.title')}
          </h2>

          <p id="cookie-consent-desc" className={styles.text}>
            <Trans
              i18nKey={hasAnalytics ? 'cookieConsent.introAnalytics' : 'cookieConsent.introBasic'}
              values={{ siteName }}
              components={{ strong: <strong /> }}
            />
          </p>

          {hasAnalytics && (
            <button
              type="button"
              className={styles.detailsToggle}
              onClick={() => setShowDetails((prev) => !prev)}
              aria-expanded={showDetails}
            >
              {showDetails ? t('cookieConsent.hideDetails') : t('cookieConsent.showDetails')}
            </button>
          )}

          {hasAnalytics && showDetails && (
            <div className={styles.details}>
              <div className={styles.category}>
                <h3 className={styles.categoryTitle}>{t('cookieConsent.essentialTitle')}</h3>
                <p className={styles.categoryText}>{t('cookieConsent.essentialDescription')}</p>
              </div>
              <div className={styles.category}>
                <h3 className={styles.categoryTitle}>{t('cookieConsent.analyticsTitle')}</h3>
                <p className={styles.categoryText}>
                  <Trans i18nKey="cookieConsent.analyticsDescription" components={{ strong: <strong /> }} />
                </p>
                <p className={styles.categoryNote}>{t('cookieConsent.analyticsDataNote')}</p>
              </div>
            </div>
          )}

          <p className={styles.policyLine}>
            <a
              href={PRIVACY_POLICY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.link}
            >
              {t('cookieConsent.learnMore')}
            </a>
          </p>

          <div className={styles.buttons}>
            {hasAnalytics ? (
              <>
                <button className={styles.declineButton} onClick={handleDecline}>
                  {t('cookieConsent.acceptEssentialOnly')}
                </button>
                <button className={styles.acceptButton} onClick={handleAccept}>
                  {t('cookieConsent.accept')}
                </button>
              </>
            ) : (
              <button className={styles.acceptButton} onClick={handleAccept}>
                {t('cookieConsent.gotIt')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
